import { ICase } from '@brands/services/cases/types/ICase';
import { MedicalConditions, MedicalHistory } from '@brands/services/clinical_data/types/types';
import { ImageWithNotes } from '@brands/services/forms/types/ImageWithNotes';
import { IEducation } from '@brands/services/oneReport/types/IEducation';
import { IOneReportInternalNote } from '@brands/services/oneReport/types/IOneReportInternalNote';
import { IOneReportMedia } from '@brands/services/oneReport/types/IOneReportMedia';
import { oralHealthCoachingTypeOptions } from '@brands/Utils/selectOptions';
import { parsePhoneNumber } from 'libphonenumber-js';
import sanitizeHtml from 'sanitize-html';

const IMAGE_FILE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tif', 'tiff', 'jfif', 'pjpeg', 'pjp'];

export const sanitizeOHCReportHtml = (html?: string | null): string =>
  sanitizeHtml(html || '', {
    allowedTags: ['b', 'strong', 'em', 'i', 'u', 'p', 'br', 'ul', 'ol', 'li', 'a', 'span'],
    allowedAttributes: {
      a: ['href', 'target', 'rel'],
      span: ['class'],
      li: ['data-list'],
    },
  });

export const getOHCPlainText = (html?: string | null): string =>
  sanitizeOHCReportHtml(html)
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|li|div|h[1-6])>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const hasOHCText = (value?: string | null): boolean => Boolean(value && value.trim() !== '');

export const hasOHCReportHtml = (html?: string | null): boolean => getOHCPlainText(html).length > 0;

export const formatOHCPhoneNumber = (phone?: string | null): string => {
  if (!phone?.trim()) return '';

  try {
    const parsed = parsePhoneNumber(phone, 'US');
    return parsed?.isValid() ? parsed.formatNational() : '';
  } catch {
    return '';
  }
};

export const getOHCInsuranceCarrier = (thisCase: ICase): string => {
  const caseCarrier =
    thisCase.insurance?.insurance_membership?.insurance_carrier?.name?.trim() ||
    thisCase.insurance_carrier?.trim() ||
    '';
  if (caseCarrier) return caseCarrier;

  return (
    thisCase.patient?.patient_profile?.insurances?.[0]?.insurance_membership?.insurance_carrier?.name?.trim() || ''
  );
};

export const getOHCMedicalConditionsText = (conditions?: Partial<MedicalConditions> | null): string => {
  if (!conditions) return '';

  const labels: string[] = [];

  oralHealthCoachingTypeOptions.forEach((option) => {
    if ((conditions as Record<string, unknown>)[option.value] === true) {
      labels.push(option.label);
    }
  });

  const otherConditions = conditions.conditions_not_listed?.trim();
  if (otherConditions && otherConditions !== 'false' && otherConditions !== 'true') {
    labels.push(otherConditions);
  }

  return labels.join(', ');
};

export const getOHCPharmacyLines = (medicalHistory?: Partial<MedicalHistory> | null): string[] => {
  if (!medicalHistory) return [];

  const cleanPharmacyValue = (value?: string | null): string => {
    const normalizedValue = value?.trim() || '';
    return normalizedValue.toLowerCase() === 'no' ? '' : normalizedValue;
  };

  const address = [
    medicalHistory.pharmacy_street_address,
    medicalHistory.pharmacy_city,
    medicalHistory.pharmacy_state,
    medicalHistory.pharmacy_zip_code,
  ]
    .map(cleanPharmacyValue)
    .filter(Boolean)
    .join(', ');

  return [
    cleanPharmacyValue(medicalHistory.pharmacy_name),
    address,
    formatOHCPhoneNumber(medicalHistory.pharmacy_phone_number),
  ].filter(Boolean);
};

export const getVisibleOHCEducations = (educations?: IEducation[] | null): IEducation[] =>
  (educations || [])
    .filter((education) => hasOHCText(education.title) || hasOHCText(education.url))
    .sort((a, b) => Number(a.id) - Number(b.id));

export const getVisibleOHCGoals = <T extends { goal_text?: string; educations?: IEducation[] }>(
  goals?: T[] | null,
): Array<T & { educations: IEducation[] }> =>
  (goals || [])
    .filter((goal) => hasOHCReportHtml(goal.goal_text))
    .map((goal) => ({
      ...goal,
      educations: getVisibleOHCEducations(goal.educations),
    }));

export const getVisibleOHCNotes = (notes?: IOneReportInternalNote[] | null): IOneReportInternalNote[] =>
  (notes || [])
    .filter((note) => !note.deleted_at && hasOHCReportHtml(note.note))
    .sort((a, b) => Number(a.id) - Number(b.id));

const getFileExtension = (value?: string): string => {
  if (!value) return '';

  const valueWithoutQuery = value.split('?')[0].split('#')[0];
  const lastPathSegment = valueWithoutQuery.split('/').pop() || '';
  const lastDotIndex = lastPathSegment.lastIndexOf('.');

  return lastDotIndex >= 0 ? lastPathSegment.slice(lastDotIndex + 1).toLowerCase() : '';
};

export const isDisplayableOHCImage = (mimetype?: string, filename?: string, src?: string): boolean => {
  const normalizedMimetype = (mimetype || '').toLowerCase();
  if (normalizedMimetype.startsWith('image/')) return true;
  if (normalizedMimetype && normalizedMimetype !== 'application/octet-stream') return false;

  const extension = getFileExtension(filename) || getFileExtension(src);
  return IMAGE_FILE_EXTENSIONS.includes(extension);
};

/**
 * The fields report rendering reads off an attachment. One Report media
 * and Oral Health Coaching attachments are separate tables with
 * different id types, so this is structural rather than either concrete
 * type.
 */
export type SelectableReportMedia = {
  id?: string | number;
  media_id?: string;
  note?: string;
  is_selected?: boolean;
  created_at?: string;
};

export const getSelectedOHCReportMedia = (media?: SelectableReportMedia[] | null): SelectableReportMedia[] =>
  (media || []).filter((item) => item.is_selected).sort((a, b) => Number(a.id) - Number(b.id));

export const getVisibleOHCImages = (images?: ImageWithNotes[] | null): ImageWithNotes[] =>
  (images || []).filter((image) => image.src && isDisplayableOHCImage(image.mimetype, image.filename, image.src));
